import React, { useState } from 'react';
import { 
  Button, 
  Container, 
  Typography, 
  Paper, 
  Box, 
  CircularProgress, 
  Stack 
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { PDFDocument } from 'pdf-lib';
import { toast } from 'react-toastify'; // You have this in your deps!

const PdfMerger = () => {
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e, setFile) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const mergePdfs = async () => {
    if (!file1 || !file2) {
      toast.warning("Please upload both PDF files");
      return;
    }

    setLoading(true);
    try {
      // Load files into ArrayBuffers
      const pdf1Bytes = await file1.arrayBuffer();
      const pdf2Bytes = await file2.arrayBuffer();

      // Initialize the merged document
      const mergedPdf = await PDFDocument.create();

      // Process first PDF
      const pdf1 = await PDFDocument.load(pdf1Bytes);
      const copiedPages1 = await mergedPdf.copyPages(pdf1, pdf1.getPageIndices());
      copiedPages1.forEach((page) => mergedPdf.addPage(page));

      // Process second PDF
      const pdf2 = await PDFDocument.load(pdf2Bytes);
      const copiedPages2 = await mergedPdf.copyPages(pdf2, pdf2.getPageIndices());
      copiedPages2.forEach((page) => mergedPdf.addPage(page));

      // Serialize to bytes and trigger download
      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `merged_${Date.now()}.pdf`;
      link.click();
      
      toast.success("PDFs merged successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to merge PDFs. The files might be encrypted.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" color="#2D68C4">
          PDF Merger
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
          Select two PDF files to combine them into a single document.
        </Typography>

        <Stack spacing={3}>
          {/* File Input 1 */}
          <Button
            component="label"
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            color={file1 ? "success" : "primary"}
            fullWidth
          >
            {file1 ? file1.name : "Upload First PDF"}
            <input type="file" hidden accept=".pdf" onChange={(e) => handleFileChange(e, setFile1)} />
          </Button>

          {/* File Input 2 */}
          <Button
            component="label"
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            color={file2 ? "success" : "primary"}
            fullWidth
          >
            {file2 ? file2.name : "Upload Second PDF"}
            <input type="file" hidden accept=".pdf" onChange={(e) => handleFileChange(e, setFile2)} />
          </Button>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
              onClick={mergePdfs}
              disabled={loading || !file1 || !file2}
              fullWidth
              sx={{ py: 1.5, borderRadius: 2 }}
            >
              {loading ? "Merging..." : "Merge & Download"}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
};

export default PdfMerger;